'use client';

import { c as _c } from "react/compiler-runtime";
import { type ComponentProps, type ReactElement, type ReactNode, type RefObject, useId, useMemo, useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';
import IconClose from '@/components/assets/icon_close.svg';
import OptimizedImage from '@/components/OptimizedImage';
import { Dialog, DialogClose, DialogOverlay, DialogPortal, DialogPrimitiveContent } from '@/components/ui/dialog';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import ViverseButton from '@/components/ViverseButton';
import { EMBED_BREAKPOINTS } from '@/constants/control-bar';
import { useEmbeddedControlBarSurface } from '@/context/EmbeddedControlBarContext';
import { cn } from '@/lib/utils';
const CONTROL_BAR_ACTION_GAP = 12;
const CONTROL_BAR_DIALOG_REFERENCE_HEIGHT = 277;
const CONTROL_BAR_DIALOG_REFERENCE_WIDTH = 720;
const CONTROL_BAR_DIALOG_WIDTH = 319;
const CONTROL_BAR_EMBED_DIALOG_HEIGHT = 264;
const CONTROL_BAR_EMBED_DIALOG_REFERENCE_WIDTH = 970;
const CONTROL_BAR_EMBED_DIALOG_WIDTH = 565;

// Embedded sizes (Figma `External Embed Sizing Specs`). They apply only inside the
// `@container/embed` iframe viewport, so outside an embed these classes do nothing. `md` is the
// iframe width below the `lg` breakpoint, `sm` below the `md` one; the breakpoints live in
// `app/[locale]/globals.css` as `--container-embed-*`.
// Figma heights are caps: each dialog is also limited to the room above the control bar
// (Radix reports it for the popovers) so a short iframe never clips it. Class names are written
// out in full because Tailwind cannot see interpolated strings.
const EMBEDDED_SHARE_DIALOG_CLASSNAME = '@max-embed-lg/embed:h-[min(260px,var(--radix-popover-content-available-height))] @max-embed-lg/embed:py-4 @max-embed-md/embed:h-[min(180px,var(--radix-popover-content-available-height))]';
const EMBEDDED_SHARE_DIALOG_POPOVER_CLASSNAME = '@max-embed-lg/embed:h-[min(260px,var(--radix-popover-content-available-height))] @max-embed-md/embed:h-[min(180px,var(--radix-popover-content-available-height))]';
const EMBEDDED_EMBED_DIALOG_CLASSNAME = '@max-embed-lg/embed:min-h-[min(260px,calc(100%-12px))] @max-embed-lg/embed:max-h-[calc(100%-12px)] @max-embed-lg/embed:overflow-y-auto @max-embed-lg/embed:w-[min(468px,calc(100cqw-12px))] @max-embed-lg/embed:px-6 @max-embed-lg/embed:py-4 @max-embed-md/embed:min-h-[min(180px,100%)] @max-embed-md/embed:max-h-full @max-embed-md/embed:w-[100cqw] @max-embed-md/embed:rounded-none @max-embed-md/embed:px-3 @max-embed-md/embed:py-2';
const CONTROL_BAR_DIALOG_SURFACE_CLASSNAME = '[background:linear-gradient(90deg,rgba(255,255,255,0.15)_0%,rgba(255,255,255,0.15)_100%),linear-gradient(90deg,rgba(19,27,38,0.7)_0%,rgba(19,27,38,0.7)_100%)] rounded-[7px] border-none p-6 text-white shadow-none backdrop-blur-[50px] outline-none';
const CONTROL_BAR_DIALOG_CLOSE_CLASSNAME = 'absolute top-0 right-0 inline-flex size-[34px] appearance-none items-center justify-center rounded-full bg-transparent';
type ControlBarActionDialogProps = ComponentProps<typeof Dialog> & {
  children: ReactNode;
  className?: string;
  closeLabel?: string;
  portalContainer?: HTMLElement | null;
  referenceHeight?: number;
  trigger: ReactElement;
};
type ControlBarLoginPopoverProps = ComponentProps<typeof Popover> & {
  badge: ReactNode;
  badgeClassName: string;
  closeLabel: string;
  description: string;
  imageAlt: string;
  imageSrc: string;
  loginLabel: string;
  onLogin: () => void;
  portalContainer?: HTMLElement | null;
  title: string;
  trigger: ReactElement;
};
type ControlBarIframeDialogProps = ComponentProps<typeof Dialog> & {
  children: ReactNode;
  closeLabel: string;
  portalContainer?: HTMLElement | null;
  title: string;
};

/**
 * Dialogs scale proportionally with the container width. In the embedded Control Bar's normal view,
 * which has the `embed` container their CSS sizes depend on, they keep that scaling from the `lg`
 * breakpoint up and drop it below. Native fullscreen has no such container and keeps the scaling.
 */
function useControlBarDialogMetrics(portalContainer, t0) {
  const $ = _c(6);
  const referenceWidth = t0 === undefined ? CONTROL_BAR_DIALOG_REFERENCE_WIDTH : t0;
  const embeddedSurface = useEmbeddedControlBarSurface();
  const t1 = portalContainer ?? null;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      current: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const portalContainerRef = t2 as RefObject<HTMLElement>;
  let t3;
  if ($[2] !== portalContainerRef) {
    t3 = {
      ref: portalContainerRef,
      box: "border-box"
    };
    $[2] = portalContainerRef;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    width: t4
  } = useResizeObserver(t3);
  const width = t4 === undefined ? referenceWidth : t4;
  const hasFixedSize = embeddedSurface === "normal" && width < EMBED_BREAKPOINTS.lg;
  const t5 = hasFixedSize ? 1 : Math.min(1, width / referenceWidth);
  let t6;
  if ($[4] !== t5) {
    t6 = {
      scale: t5
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  return t6;
}
export function ControlBarIframeDialog(t0) {
  const $ = _c(31);
  let children;
  let closeLabel;
  let onOpenChange;
  let open;
  let portalContainer;
  let props;
  let title;
  if ($[0] !== t0) {
    ({
      children,
      closeLabel,
      onOpenChange,
      open,
      portalContainer,
      title,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = closeLabel;
    $[3] = onOpenChange;
    $[4] = open;
    $[5] = portalContainer;
    $[6] = props;
    $[7] = title;
  } else {
    children = $[1];
    closeLabel = $[2];
    onOpenChange = $[3];
    open = $[4];
    portalContainer = $[5];
    props = $[6];
    title = $[7];
  }
  const {
    scale: dialogScale
  } = useControlBarDialogMetrics(portalContainer, CONTROL_BAR_EMBED_DIALOG_REFERENCE_WIDTH);
  const t1 = portalContainer ?? undefined;
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogOverlay className="z-overlay absolute bg-transparent" />;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("relative shrink-0", CONTROL_BAR_DIALOG_SURFACE_CLASSNAME, EMBEDDED_EMBED_DIALOG_CLASSNAME);
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = `scale(${dialogScale})`;
  let t5;
  if ($[10] !== t4) {
    t5 = {
      width: CONTROL_BAR_EMBED_DIALOG_WIDTH,
      minHeight: CONTROL_BAR_EMBED_DIALOG_HEIGHT,
      transform: t4,
      transformOrigin: "center"
    };
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IconClose className="size-5" aria-hidden="true" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== closeLabel) {
    t7 = <DialogClose className="absolute top-2 right-2 inline-flex size-12 items-center justify-center rounded-full bg-transparent" aria-label={closeLabel}>{t6}</DialogClose>;
    $[13] = closeLabel;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== children || $[16] !== t5 || $[17] !== t7) {
    t8 = <div className={t3} style={t5}>{children}{t7}</div>;
    $[15] = children;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== dialogScale || $[20] !== t8 || $[21] !== title) {
    t9 = <DialogPrimitiveContent aria-label={title} data-component="control-bar-iframe-dialog" data-dialog-scale={dialogScale} className="z-modal absolute inset-0 flex items-center justify-center border-none bg-transparent p-0 shadow-none outline-none">{t8}</DialogPrimitiveContent>;
    $[19] = dialogScale;
    $[20] = t8;
    $[21] = title;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t1 || $[24] !== t9) {
    t10 = <DialogPortal container={t1}>{t2}{t9}</DialogPortal>;
    $[23] = t1;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== onOpenChange || $[27] !== open || $[28] !== props || $[29] !== t10) {
    t11 = <Dialog open={open} onOpenChange={onOpenChange} {...props}>{t10}</Dialog>;
    $[26] = onOpenChange;
    $[27] = open;
    $[28] = props;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
}
export function ControlBarActionDialog(t0) {
  const $ = _c(43);
  let children;
  let className;
  let closeLabel;
  let onOpenChange;
  let open;
  let portalContainer;
  let props;
  let referenceHeight;
  let trigger;
  if ($[0] !== t0) {
    ({
      children,
      className,
      closeLabel,
      onOpenChange,
      open,
      portalContainer,
      referenceHeight,
      trigger,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = closeLabel;
    $[4] = onOpenChange;
    $[5] = open;
    $[6] = portalContainer;
    $[7] = props;
    $[8] = referenceHeight;
    $[9] = trigger;
  } else {
    children = $[1];
    className = $[2];
    closeLabel = $[3];
    onOpenChange = $[4];
    open = $[5];
    portalContainer = $[6];
    props = $[7];
    referenceHeight = $[8];
    trigger = $[9];
  }
  const {
    scale: dialogScale
  } = useControlBarDialogMetrics(portalContainer, CONTROL_BAR_DIALOG_REFERENCE_WIDTH);
  const anchorRef = useRef(null);
  let t1;
  if ($[10] !== closeLabel || $[11] !== onOpenChange) {
    t1 = closeLabel && <button type="button" className={CONTROL_BAR_DIALOG_CLOSE_CLASSNAME} aria-label={closeLabel} onClick={() => onOpenChange?.(false)}><IconClose className="size-5" aria-hidden="true" /></button>;
    $[10] = closeLabel;
    $[11] = onOpenChange;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== children || $[14] !== t1) {
    t2 = <>{children}{t1}</>;
    $[13] = children;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const content = t2;
  let t3;
  if ($[16] !== trigger) {
    t3 = <PopoverAnchor asChild={true}><span ref={anchorRef} className="inline-flex">{trigger}</span></PopoverAnchor>;
    $[16] = trigger;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const t4 = CONTROL_BAR_ACTION_GAP * dialogScale;
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event => {
      if (event.target instanceof Node && anchorRef.current?.contains(event.target)) {
        event.preventDefault();
      }
    };
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const t6 = CONTROL_BAR_DIALOG_WIDTH * dialogScale;
  const t7 = (referenceHeight ?? CONTROL_BAR_DIALOG_REFERENCE_HEIGHT) * dialogScale;
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = {
      width: t6,
      height: t7
    };
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("z-popover border-none bg-transparent p-0 shadow-none", EMBEDDED_SHARE_DIALOG_POPOVER_CLASSNAME);
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== className) {
    t10 = cn("absolute bottom-0 left-1/2 w-[319px]", CONTROL_BAR_DIALOG_SURFACE_CLASSNAME, className, EMBEDDED_SHARE_DIALOG_CLASSNAME);
    $[23] = className;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = `translateX(-50%) scale(${dialogScale})`;
  let t12;
  if ($[25] !== t11) {
    t12 = {
      transform: t11,
      transformOrigin: "bottom center"
    };
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== content || $[28] !== t10 || $[29] !== t12) {
    t13 = <div className={t10} style={t12}>{content}</div>;
    $[27] = content;
    $[28] = t10;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== dialogScale || $[32] !== portalContainer || $[33] !== t13 || $[34] !== t4 || $[35] !== t8) {
    t14 = <PopoverContent portalContainer={portalContainer} side="top" sideOffset={t4} align="center" collisionBoundary={portalContainer} collisionPadding={0} onInteractOutside={t5} role="dialog" data-component="control-bar-action-dialog" data-anchor-gap={CONTROL_BAR_ACTION_GAP} data-dialog-scale={dialogScale} style={t8} className={t9}>{t13}</PopoverContent>;
    $[31] = dialogScale;
    $[32] = portalContainer;
    $[33] = t13;
    $[34] = t4;
    $[35] = t8;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== onOpenChange || $[38] !== open || $[39] !== props || $[40] !== t14 || $[41] !== t3) {
    t15 = <Popover open={open} onOpenChange={onOpenChange} {...props}>{t3}{t14}</Popover>;
    $[37] = onOpenChange;
    $[38] = open;
    $[39] = props;
    $[40] = t14;
    $[41] = t3;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
}
export function ControlBarLoginPopover(t0) {
  const $ = _c(73);
  let badge;
  let badgeClassName;
  let closeLabel;
  let description;
  let imageAlt;
  let imageSrc;
  let loginLabel;
  let onLogin;
  let onOpenChange;
  let portalContainer;
  let props;
  let title;
  let trigger;
  if ($[0] !== t0) {
    ({
      badge,
      badgeClassName,
      closeLabel,
      description,
      imageAlt,
      imageSrc,
      loginLabel,
      onLogin,
      onOpenChange,
      portalContainer,
      title,
      trigger,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = badge;
    $[2] = badgeClassName;
    $[3] = closeLabel;
    $[4] = description;
    $[5] = imageAlt;
    $[6] = imageSrc;
    $[7] = loginLabel;
    $[8] = onLogin;
    $[9] = onOpenChange;
    $[10] = portalContainer;
    $[11] = props;
    $[12] = title;
    $[13] = trigger;
  } else {
    badge = $[1];
    badgeClassName = $[2];
    closeLabel = $[3];
    description = $[4];
    imageAlt = $[5];
    imageSrc = $[6];
    loginLabel = $[7];
    onLogin = $[8];
    onOpenChange = $[9];
    portalContainer = $[10];
    props = $[11];
    title = $[12];
    trigger = $[13];
  }
  const titleId = useId();
  const descriptionId = useId();
  const {
    scale: dialogScale
  } = useControlBarDialogMetrics(portalContainer, CONTROL_BAR_DIALOG_REFERENCE_WIDTH);
  const anchorRef = useRef(null);
  let t1;
  if ($[14] !== trigger) {
    t1 = <PopoverAnchor asChild={true}><span ref={anchorRef} className="inline-flex">{trigger}</span></PopoverAnchor>;
    $[14] = trigger;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  const t2 = CONTROL_BAR_ACTION_GAP * dialogScale;
  let t3;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => {
      if (event.target instanceof Node && anchorRef.current?.contains(event.target)) {
        event.preventDefault();
      }
    };
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const t4 = CONTROL_BAR_DIALOG_WIDTH * dialogScale;
  const t5 = CONTROL_BAR_DIALOG_REFERENCE_HEIGHT * dialogScale;
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = {
      width: t4,
      height: t5
    };
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("absolute bottom-0 left-1/2 flex h-[277px] w-[319px] flex-col", CONTROL_BAR_DIALOG_SURFACE_CLASSNAME);
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = `translateX(-50%) scale(${dialogScale})`;
  let t9;
  if ($[21] !== t8) {
    t9 = {
      transform: t8,
      transformOrigin: "bottom center"
    };
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== imageAlt || $[24] !== imageSrc) {
    t10 = <div className="absolute top-[11px] left-1/2 h-[72px] w-[129px] -translate-x-[calc(50%+3px)] overflow-hidden rounded-lg"><OptimizedImage src={imageSrc} alt={imageAlt} fill={true} sizes="129px" className="object-cover" /></div>;
    $[23] = imageAlt;
    $[24] = imageSrc;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== badgeClassName) {
    t11 = cn("absolute top-[55px] left-[101px] flex size-9 items-center justify-center overflow-hidden rounded-full p-2", badgeClassName);
    $[26] = badgeClassName;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== badge || $[29] !== t11) {
    t12 = <div className={t11}>{badge}</div>;
    $[28] = badge;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t10 || $[32] !== t12) {
    t13 = <div className="relative h-[99px] w-[139px] shrink-0">{t10}{t12}</div>;
    $[31] = t10;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== title || $[35] !== titleId) {
    t14 = <h2 id={titleId} className="w-full truncate text-base leading-6 font-semibold tracking-[0.2px]">{title}</h2>;
    $[34] = title;
    $[35] = titleId;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== description || $[38] !== descriptionId) {
    t15 = <p id={descriptionId} className="w-full text-xs leading-4 font-normal tracking-[0.15px] text-white">{description}</p>;
    $[37] = description;
    $[38] = descriptionId;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t14 || $[41] !== t15) {
    t16 = <div className="flex w-full flex-col items-center gap-2 text-center">{t14}{t15}</div>;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== loginLabel || $[44] !== onLogin) {
    t17 = <ViverseButton className="h-[34px] w-full max-w-[400px] min-w-20 px-4 py-1 text-sm leading-[22px] font-medium tracking-[0.15px]" size="custom" onClick={onLogin}>{loginLabel}</ViverseButton>;
    $[43] = loginLabel;
    $[44] = onLogin;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t13 || $[47] !== t16 || $[48] !== t17) {
    t18 = <div className="flex w-full flex-col items-center gap-4">{t13}{t16}{t17}</div>;
    $[46] = t13;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== onOpenChange) {
    t19 = () => onOpenChange?.(false);
    $[50] = onOpenChange;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconClose className="size-5" aria-hidden="true" />;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== closeLabel || $[54] !== t19) {
    t21 = <button type="button" className={CONTROL_BAR_DIALOG_CLOSE_CLASSNAME} aria-label={closeLabel} onClick={t19}>{t20}</button>;
    $[53] = closeLabel;
    $[54] = t19;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== t18 || $[57] !== t21 || $[58] !== t9) {
    t22 = <div className={t7} style={t9}>{t18}{t21}</div>;
    $[56] = t18;
    $[57] = t21;
    $[58] = t9;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== descriptionId || $[61] !== dialogScale || $[62] !== portalContainer || $[63] !== t2 || $[64] !== t22 || $[65] !== t6 || $[66] !== titleId) {
    t23 = <PopoverContent portalContainer={portalContainer} side="top" sideOffset={t2} align="center" collisionBoundary={portalContainer} collisionPadding={0} onInteractOutside={t3} aria-labelledby={titleId} aria-describedby={descriptionId} data-component="control-bar-login-popover" data-anchor-gap={CONTROL_BAR_ACTION_GAP} data-dialog-scale={dialogScale} style={t6} className="z-popover border-none bg-transparent p-0 shadow-none">{t22}</PopoverContent>;
    $[60] = descriptionId;
    $[61] = dialogScale;
    $[62] = portalContainer;
    $[63] = t2;
    $[64] = t22;
    $[65] = t6;
    $[66] = titleId;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== onOpenChange || $[69] !== props || $[70] !== t1 || $[71] !== t23) {
    t24 = <Popover onOpenChange={onOpenChange} {...props}>{t1}{t23}</Popover>;
    $[68] = onOpenChange;
    $[69] = props;
    $[70] = t1;
    $[71] = t23;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  return t24;
}